import { useState, useMemo, useCallback } from 'react';
import type { Study } from '@/models/study';
import { useSettings } from '@/features/settings';
import { usePendingStudyLoad } from '@/features/study-detail';
import { FilterChip } from '@/shared/ui/FilterChip';
import SortDropdown from './SortDropdown/SortDropdown';
import type { SortKey } from './SortDropdown/SortDropdown';
import WorklistRow from './WorklistRow/WorklistRow';
import styles from './WorklistRail.module.css';

export type FilterPill =
  | 'assigned'
  | 'pending'
  | 'finalized'
  | 'stat'
  | 'urgent';

const FILTER_OPTIONS: {
  value: FilterPill;
  label: string;
  dotColor?: string;
}[] = [
  { value: 'assigned', label: 'Assigned to me' },
  { value: 'pending', label: 'Pending' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'stat', label: 'Stat' },
  { value: 'urgent', label: 'Urgent' },
];

const PAGE_SIZE = 20;
const FILTER_STORAGE_KEY = 'worklist-filters';
// AP-852: no filters selected out of the box, so a new user sees the whole
// worklist before they know what the pills do. Selections still persist
// per browser under FILTER_STORAGE_KEY once the user picks any.
const DEFAULT_FILTERS: FilterPill[] = [];

function loadFilters(): FilterPill[] {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as FilterPill[];
  } catch {
    // ignore
  }
  return DEFAULT_FILTERS;
}

const PRIORITY_ORDER: Record<string, number> = {
  STAT: 0,
  URGENT: 1,
  ROUTINE: 2,
};

function sortStudies(list: Study[], key: SortKey): Study[] {
  return [...list].sort((a, b) => {
    switch (key) {
      case 'priority':
        return (
          (PRIORITY_ORDER[a.priority ?? ''] ?? 3) -
          (PRIORITY_ORDER[b.priority ?? ''] ?? 3)
        );
      case 'date':
        return b.studyDate.localeCompare(a.studyDate);
      case 'name':
        return `${a.patientLastName} ${a.patientFirstName}`.localeCompare(
          `${b.patientLastName} ${b.patientFirstName}`
        );
      default:
        return 0;
    }
  });
}

interface WorklistRailProps {
  studies: Study[];
  activeStudyUuid?: string;
}

export default function WorklistRail({
  studies,
  activeStudyUuid,
}: WorklistRailProps) {
  const { settings } = useSettings();
  // Click-time highlight: the row click flags the target study synchronously
  // (flushSync in WorklistRow), so the highlight jumps to it in the SAME
  // frame as the click — the URL param catches up when the router transition
  // lands, and the gesture is cleared once the study is revealed.
  const pendingStudyLoad = usePendingStudyLoad();
  const highlightUuid = pendingStudyLoad?.uuid ?? activeStudyUuid;
  const [activeFilters, setActiveFilters] = useState<FilterPill[]>(loadFilters);
  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const handleFilterChange = useCallback((values: FilterPill[]) => {
    setActiveFilters(values);
    setVisibleCount(PAGE_SIZE);
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(values));
  }, []);

  const filteredStudies = useMemo(() => {
    if (activeFilters.length === 0) return studies;

    const hasAssigned = activeFilters.includes('assigned');
    const hasPending = activeFilters.includes('pending');
    const hasFinalized = activeFilters.includes('finalized');
    const hasStat = activeFilters.includes('stat');
    const hasUrgent = activeFilters.includes('urgent');
    const hasStatusFilter = hasPending || hasFinalized;
    const hasPriorityFilter = hasStat || hasUrgent;

    return studies.filter((study) => {
      if (hasAssigned && !study.assignedToMe) return false;

      if (hasStatusFilter) {
        const status = (study.reportStatus ?? '').toUpperCase();
        const isFinal = status === 'FINAL' || status === 'FINALIZED';
        const matchesStatus =
          (hasFinalized && isFinal) || (hasPending && !isFinal);
        if (!matchesStatus) return false;
      }

      if (hasPriorityFilter) {
        const p = study.priority ?? '';
        const matchesPriority =
          (hasStat && p === 'STAT') || (hasUrgent && p === 'URGENT');
        if (!matchesPriority) return false;
      }

      return true;
    });
  }, [studies, activeFilters]);

  const sortedStudies = useMemo(
    () => sortStudies(filteredStudies, sortKey),
    [filteredStudies, sortKey]
  );

  const visibleStudies = sortedStudies.slice(0, visibleCount);
  const remaining = sortedStudies.length - visibleCount;

  const countLabel = useMemo(() => {
    const labelMap: Record<FilterPill, string> = {
      assigned: 'ASSIGNED',
      pending: 'PENDING',
      finalized: 'FINALIZED',
      stat: 'STAT',
      urgent: 'URGENT',
    };
    const parts = activeFilters.map((f) => labelMap[f]);
    const filterText = parts.length > 0 ? parts.join(' + ') : 'ALL';
    return `${sortedStudies.length} ${filterText}`;
  }, [activeFilters, sortedStudies.length]);

  return (
    <div className={styles.worklistRail}>
      <div className={styles.stickyControls}>
        <div className={styles.chipRow}>
          {FILTER_OPTIONS.map((opt) => (
            <FilterChip
              key={opt.value}
              label={opt.label}
              selected={activeFilters.includes(opt.value)}
              dotColor={opt.dotColor}
              onChange={(checked) => {
                const next = checked
                  ? [...activeFilters, opt.value]
                  : activeFilters.filter((v) => v !== opt.value);
                handleFilterChange(next);
              }}
            />
          ))}
        </div>

        <div className={styles.countRow}>
          <span className={styles.countLabel}>{countLabel}</span>
          <SortDropdown value={sortKey} onChange={setSortKey} />
        </div>
      </div>

      {sortedStudies.length === 0 && (
        <div className={styles.emptyState}>No studies match filters</div>
      )}
      {visibleStudies.length > 0 && (
        <ul className={styles.list}>
          {visibleStudies.map((study) => (
            <WorklistRow
              key={study.uuid}
              study={study}
              isActive={study.uuid === highlightUuid}
              compact={settings.compactMode}
            />
          ))}
        </ul>
      )}
      {remaining > 0 && (
        <button
          type="button"
          className={styles.showMore}
          onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
        >
          Show {Math.min(remaining, PAGE_SIZE)} more
        </button>
      )}
    </div>
  );
}
